import { Search as SearchIcon } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Container, Grid, Typography } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import Cat from '../../assets/images/Cat.svg';
import Chat from '../../assets/images/chat.svg';
import CreditCard from '../../assets/images/credit-card.svg';
import GP from '../../assets/images/google-play.png';
import HeroPageDogOne from '../../assets/images/HeroPageDogOne.svg';
import DogTwo from '../../assets/images/HeroPageDogTwo.svg';
import IOS from '../../assets/images/IOS.png';
import paw_one from '../../assets/images/paw_one.svg';
import Searching from '../../assets/images/searching.svg';

const Landing: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <img src={Searching} alt="Searching" width="54px" height="54px" />,
      title: 'Discover',
      description:
        'Explore verified sitters from fellow pet parents and select a sitter that perfectly matches your needs, ensuring your furry companions.',
    },
    {
      icon: <img src={CreditCard} alt="Credit Card" width="54px" height="54px" />,
      title: 'Book & Pay',
      description:
        'Say goodbye to cash or checks. Our user-friendly website and app streamline the booking process and enable you to make secure payments effortlessly.',
    },
    {
      icon: <img src={Chat} alt="Chat" width="54px" height="54px" />,
      title: 'Relax',
      description:
        'Relax and enjoy the freedom of knowing your pets are well-cared for during your absence.',
    },
  ];

  return (
    <Box sx={{ backgroundColor: '#FAF8F5' }}>
      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ pt: 8, pb: 12 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                color: '#178B8E',
                mb: 3,
              }}
            >
              Leave your pets in caring hands
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: '1.1rem',
                color: '#5A5A5A',
                mb: 4,
                lineHeight: 1.7,
              }}
            >
              Take the first step in finding the perfect pet sitter. Your pets' happiness and
              well-being are our top priorities, and we are here to make their care seamless and
              worry-free.
            </Typography>
            <Button
              variant="contained"
              size="large"
              endIcon={<SearchIcon />}
              onClick={() => navigate('/services')}
              sx={{
                backgroundColor: '#178B8E',
                fontSize: '1.1rem',
                px: 4,
                py: 1.5,
                '&:hover': {
                  backgroundColor: '#0F6164',
                },
              }}
            >
              Our Service
            </Button>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', alignItems: 'flex-end' }}>
              <Box
                component="img"
                src={HeroPageDogOne}
                alt="Dog 1 img"
                sx={{ width: { xs: '55%', md: '315px' }, maxWidth: '315px', height: 'auto' }}
              />
              <Box
                component="img"
                src={Cat}
                alt="Cat img"
                sx={{ width: { xs: '35%', md: '201px' }, maxWidth: '201px', height: 'auto' }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Features Section */}
      <Box sx={{ backgroundColor: '#FFFFFF', py: 10 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            sx={{
              mb: 6,
              fontSize: { xs: '1.8rem', md: '2rem' },
            }}
          >
            Introduce your pets to compassionate local sitters.
          </Typography>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid size={{ xs: 12, md: 4 }} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    p: 3,
                    backgroundColor: '#FAF8F5',
                    boxShadow: 'none',
                    border: '1px solid #E5E5E5',
                    '&:hover': {
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                      transform: 'translateY(-4px)',
                      transition: 'all 0.3s ease',
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                    <Typography
                      variant="h5"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: '#178B8E',
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#5A5A5A', lineHeight: 1.7 }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Your Pets Deserve Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              component="img"
              src={DogTwo}
              alt="White Dog in Blue Bucket"
              sx={{ width: '100%', maxWidth: '320px', height: 'auto', display: 'block' }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                mb: 3,
              }}
            >
              Your Pets Deserve the Best Care
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: '1.1rem',
                color: '#5A5A5A',
                lineHeight: 1.8,
              }}
            >
              Whether you're planning a vacation, have a busy work schedule, or simply need someone
              to look after your furry friends, our platform connects you with experienced and
              trustworthy pet sitters in your area.
            </Typography>
          </Grid>
        </Grid>
      </Container>

      {/* Download App Section */}
      <Box sx={{ backgroundColor: '#FFFFFF', py: 10 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  color: '#178B8E',
                  mb: 3,
                }}
              >
                Coming soon <br />
                HappyPaws App
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: '1.1rem',
                  color: '#5A5A5A',
                  mb: 4,
                  lineHeight: 1.8,
                }}
              >
                Our company will soon launch the HappyPaws App. Stay tuned for a seamless pet care
                experience at your fingertips.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  sx={{
                    gap: 2,
                    backgroundColor: '#1A1A1A',
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: '#000',
                    },
                  }}
                >
                  <img
                    src={IOS}
                    alt="iOS App Store"
                    style={{
                      maxWidth: '27px',
                      maxHeight: '30px',
                      objectFit: 'cover',
                      objectPosition: 'center',
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '1.1rem',
                      color: '#fff',
                      lineHeight: 1.8,
                    }}
                  >
                    App Store
                  </Typography>
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    gap: 2,
                    backgroundColor: '#1A1A1A',
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: '#000',
                    },
                  }}
                >
                  <img
                    src={GP}
                    alt="Google Play Store"
                    style={{
                      maxWidth: '27px',
                      maxHeight: '30px',
                      objectFit: 'cover',
                      objectPosition: 'center',
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '1.1rem',
                      color: '#fff',
                      lineHeight: 1.8,
                    }}
                  >
                    Google Play
                  </Typography>
                </Button>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              {/* Phone mockup placeholder */}
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 300,
                  height: { xs: 420, md: 600 },
                  backgroundColor: '#1A1A1A',
                  borderRadius: 8,
                  margin: '0 auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  border: '8px solid #1A1A1A',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                }}
              >
                <Box
                  sx={{
                    width: '90%',
                    height: '95%',
                    backgroundColor: '#FFFFFF',
                    borderRadius: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <img src={paw_one} alt="paw print logo" width="103px" height="115px" />
                    <Typography
                      sx={{
                        color: '#178B8E',
                        fontWeight: 700,
                        fontSize: '1.5rem',
                      }}
                    >
                      HappyPaws
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Landing;
